// src/components/agent/FilePane.js
//
// Replaces CanvasPane (frontend spec §8.9) as a true layout column — never a
// fixed overlay on desktop. The per-kind rendering (xlsx, docx, images, pdf,
// text previews, and a download card for everything else) now lives in the
// shared FileViewer so other surfaces show files identically; FilePane owns
// only the pane chrome — the collapsed rail, the header bar, and the column
// layout — plus the line-wrap preference it hands down for text files.
import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Tooltip, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WrapTextIcon from '@mui/icons-material/WrapText';
import FileViewer, { viewerKindFor } from '../common/FileViewer';

// Kept exported here so callers that reach for the pane's kind detection keep
// working after the move.
export { viewerKindFor };

function FilePane({
  file,           // { url, gcsPath, filename, contentType, description, toolUseId, sandboxPath }
  collapsed,
  onToggleCollapse,
  onClose,
  onOp,
  onLoad,
  workbookRef,
  hasEdits = false,
  // Download exports the live workbook (not the fetched bytes) — true while there
  // are unsent edits and after they have been sent, since the pane still shows them.
  exportLive = false,
  lockReason = null, // non-null → the workbook is read-only, shown with this message
  // non-null → Close is disabled, with this as its tooltip. Collapsing stays
  // available: it keeps the workbook mounted, so it loses nothing.
  closeBlockedReason = null,
  mobile = false,
  onMobileBack,
}) {
  const [textWrap, setTextWrap] = useState(true);
  const viewerRef = useRef(null);

  const kind = viewerKindFor(file?.filename);
  // Only the spreadsheet is ever editable, so the lock is only ever shown there.
  // Disabling FortuneSheet's allowEdit in place keeps its in-memory sheets — it
  // re-reads the file only when it holds no sheets at all — so edits made
  // before the lock survive it and are still the ones sent afterwards.
  const locked = kind === 'xlsx' && Boolean(lockReason);

  // FortuneSheet measures its canvas on window resize. The editor remains
  // mounted at zero width while collapsed, so ask it to measure again once
  // the pane has regained its visible width.
  useEffect(() => {
    if (collapsed) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [collapsed]);

  if (!file) return null;

  return (
    <>
      {/* The rail and content pane are permanent siblings. Changing `collapsed`
          alters only their widths, so FileViewer and its live workbook never
          unmount. Mobile uses the existing reopen control instead of a rail. */}
      <Box
        onClick={!mobile && collapsed ? onToggleCollapse : undefined}
        aria-hidden={mobile || !collapsed}
        sx={{
          width: !mobile && collapsed ? 40 : 0,
          minWidth: 0,
          flexShrink: 0,
          borderLeft: !mobile && collapsed ? 1 : 0,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.5,
          gap: 1,
          cursor: !mobile && collapsed ? 'pointer' : 'default',
          overflow: 'hidden',
          visibility: !mobile && collapsed ? 'visible' : 'hidden',
          pointerEvents: !mobile && collapsed ? 'auto' : 'none',
          '&:hover': { bgcolor: 'background.alt' },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary', transform: 'rotate(180deg)' }} />
        <Typography
          variant="caption"
          noWrap
          sx={{
            writingMode: 'vertical-rl',
            color: 'text.secondary',
            maxHeight: 320,
          }}
        >
          {file.filename}{hasEdits ? ' (edited)' : ''}
        </Typography>
      </Box>

      <Box
        aria-hidden={collapsed || undefined}
        sx={mobile ? {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: collapsed ? 0 : '100%',
          minWidth: 0,
          zIndex: 20,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          visibility: collapsed ? 'hidden' : 'visible',
          pointerEvents: collapsed ? 'none' : 'auto',
        } : {
          width: collapsed ? 0 : '50%',
          minWidth: 0,
          flexShrink: 0,
          borderLeft: collapsed ? 0 : 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          visibility: collapsed ? 'hidden' : 'visible',
          pointerEvents: collapsed ? 'none' : 'auto',
        }}
      >
        {/* Header bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48,
          flexShrink: 0,
        }}>
          {mobile && (
            <Tooltip title="Back to chat">
              <IconButton size="small" onClick={onMobileBack}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle2" noWrap>{file.filename}</Typography>
              {hasEdits && (
                <Chip label="Edited" size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
              )}
            </Box>
            {file.description && (
              <Typography variant="caption" color="text.secondary" noWrap>{file.description}</Typography>
            )}
          </Box>
          {kind === 'text' && (
            <Tooltip title={textWrap ? 'Disable line wrap' : 'Enable line wrap'}>
              <IconButton size="small" onClick={() => setTextWrap((w) => !w)} color={textWrap ? 'primary' : 'default'}>
                <WrapTextIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => viewerRef.current?.download()}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!mobile && (
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={onToggleCollapse}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* The span lets the tooltip show over a disabled button. */}
          <Tooltip title={closeBlockedReason || 'Close'}>
            <span>
              <IconButton size="small" aria-label="Close" onClick={onClose} disabled={Boolean(closeBlockedReason)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Content area — the shared viewer owns the fetch, the per-kind render,
            and the download; errors surface inside it. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <FileViewer
            ref={viewerRef}
            file={file}
            editable={kind === 'xlsx' && !locked} // only xlsx participates in edit tracking
            onOp={onOp}
            onLoad={onLoad}
            workbookRef={workbookRef}
            exportLive={exportLive}
            textWrap={textWrap}
          />
          {locked && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                pointerEvents: 'none', // scrolling and selection stay available
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pt: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  boxShadow: 1,
                }}
              >
                {lockReason}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

export default FilePane;
